import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { navigationConfig } from './navigationConfig';
import ProfileAvatar from './ProfileAvatar';

interface MobileMenuProps {
  userProfile: {
    userId: string;
    displayName?: string;
    firstName?: string;
    email?: string;
  };
  onSignOut: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ userProfile, onSignOut }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* Hamburger Menu Button */}
      <button
        onClick={toggleMenu}
        className="text-[#73869E] rounded-lg border border-transparent hover:bg-[#3D4758] hover:border-coral-pink/20 transition-[background-color,border-color] duration-150 p-2 sm:hidden"
        aria-label="Menu"
      >
        <Menu className="w-6 h-6" />
      </button>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 sm:hidden"
          onClick={handleBackdropClick}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

          {/* Menu Panel */}
          <div className="absolute top-0 right-0 h-full w-64 bg-[#2D3846] shadow-2xl">
            {/* Menu Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-600">
              <h2 className="text-white font-medium">Menu</h2>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white hover:bg-[#3D4758] transition duration-200 p-1 rounded"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Menu Content */}
            <div className="p-4 space-y-4">
              {/* Profile Section */}
              <Link
                to="/profile"
                className="flex items-center space-x-3 p-3 bg-[#3D4758] rounded-lg hover:bg-[#4A5568] transition duration-200"
              >
                <ProfileAvatar userProfile={userProfile} />
                <div className="text-white">
                  <div className="font-medium text-sm">
                    {userProfile.displayName || userProfile.firstName || 'Profile'}
                  </div>
                  <div className="text-xs text-gray-300">
                    View Profile
                  </div>
                </div>
              </Link>

              {/* Navigation Items */}
              <div className="space-y-2">
                {/* Settings - Only item in mobile menu now */}
                <Link
                  to="/settings"
                  className={`flex items-center space-x-3 p-3 rounded-lg transition duration-200 ${
                    location.pathname === '/settings'
                      ? 'bg-[#3D4758] text-white'
                      : 'text-gray-300 hover:bg-[#3D4758] hover:text-white'
                  }`}
                >
                  <img src={navigationConfig.secondary[0].icon as string} alt="Settings" className="w-5 h-5" />
                  <span className="text-sm font-medium">Settings</span>
                </Link>
              </div>

              {/* Logout Button */}
              <div className="pt-4 border-t border-gray-600">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    onSignOut();
                  }}
                  className="w-full text-left flex items-center space-x-3 p-3 rounded-lg text-gray-300 hover:bg-[#3D4758] hover:text-white transition duration-200"
                >
                  <span className="text-sm font-medium">Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileMenu;